/**
 * This section is labeled mattress-stamped-reviews but we are in the process of
 * moving over to reviews io.
 */

/**
 * Grabs reviews from review io's API, through edgemesh's cache.
 */
class ReviewsIOStrategy {
  /**
   * Any variant SKU will work to get all products in that same group.
   */
  constructor(productSKU, pageSize, useEMCache = false) {
    this.productSKU = productSKU;
    this.pageSize = pageSize;
    this.store = 'nolah-mattress';
    this.minRating = 3;
    this.routeParams = `store=${this.store}&type=product_review&sku=${this.productSKU}&per_page=${this.pageSize}&minRating=${this.minRating}`;

    this.getReviewsEndpoint = `https://api.reviews.io/reviews?${this.routeParams}`;

    // Turn on em cache if desired.
    // Make sure not to use em-cgi cache if not on the live theme.
    if (
      useEMCache &&
      window.Shopify &&
      window.Shopify.theme &&
      window.Shopify.theme.role === 'main'
    ) {
      // The endpoint where we request reviews data from. We use edgemesh's em-cgi
      // cache route to help prevent reviews io server errors from reaching users.
      this.getReviewsEndpoint = `/em-cgi/cache/259200/api.reviews.io/product/review?${this.routeParams}`;
    }
  }

  async getReviews(page) {
    return fetch(this.getReviewsEndpoint + `&page=${page}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(res.statusText);
        }
        return res;
      })
      .then(res => res.json());
  }
}

/**
 * Grabs reviews from the metafield with the first 25 reviews in JSON format.
 */
class MetafieldReviewsStrategy {
  constructor(jsonTemplate, pageSize) {
    try {
      this.reviewsJSON = JSON.parse(jsonTemplate.innerHTML);

      // Split JSON into two pieces, the data array and the rest of the obj.
      const { data, ...reviewsObjWithoutData } = this.reviewsJSON;
      this.reviewsObjWithoutData = reviewsObjWithoutData;
      this.reviewsData = data;
    } catch (err) {
      throw new Error(
        'Metafields for reviews may not be in the correct format'
      );
    }

    this.pageSize = pageSize;
  }

  getReviews(page) {
    // The metafields cache has less reviews than all the reviews in the database
    // so bound the shown page number with modulo.
    const numReviews = this.reviewsData.length;
    const numPossiblePages = Math.ceil(numReviews / this.pageSize);
    const pageToShow = ((page - 1) % numPossiblePages) + 1;

    let startIndex = (pageToShow - 1) * this.pageSize;
    let endIndex = startIndex + this.pageSize;

    // For if we don't use modulo.
    /**
    // Make sure the endIndex doesn't exceed the number of reviews.
    if (endIndex > numReviews) {
      endIndex = numReviews;
    }

    // Return the last possible page if the page exceeds the review count.
    if (startIndex > numReviews - 1) {
      startIndex = numReviews - this.pageSize;
    }

    // Make sure not to go below 0.
    startIndex = startIndex < 0 ? 0 : startIndex;
    */

    const slicedResults = this.reviewsData.slice(startIndex, endIndex);
    return {
      data: slicedResults,
      ...this.reviewsObjWithoutData,
    };
  }
}

class MatStampedReviews extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  async connectedCallback() {
    if (this.isConnected) {
      this.cacheElements();
    }

    if (this.isConnected && !this.initialized) {
      this.initialized = true;

      try {
        await this.initReviews();
        this.attachListeners();
      } catch (err) {
        console.warn(
          'May be displaying cached reviews due to an exception',
          err
        );
      }
    }
  }

  async disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  reinit() {
    this.cacheElements();
    this.initReviews();
  }

  cacheElements() {
    this.reviewsContainer = this.querySelector('.MatStampedReviews__Reviews');
    this.paginationArea = this.querySelector('.MSReviewsPagination');
    this.paginationLinks = this.querySelector(
      '.MSReviewsPagination__PageLinks'
    );
    this.cacheSVGs();

    this.currentPage = 1;
    this.pageSize = 5;
    this.productID = this.dataset.productId;
    this.anyVariantSKU = this.dataset.sku;

    // Flag for whether we want to even consider metafields. If we need to change
    // this often then we can use the customizer for this.
    this.getReviewsFromMetafields = false;

    // Choose the strategy for getting reviews
    const metafieldReviewsTemplate = this.querySelector(
      '[data-first-pages-json]'
    );
    if (this.getReviewsFromMetafields && metafieldReviewsTemplate) {
      try {
        this.manager = new MetafieldReviewsStrategy(
          metafieldReviewsTemplate,
          this.pageSize
        );
      } catch (err) {
        console.error('Using api routes due to exception.', err);
        this.manager = new ReviewsIOStrategy(this.productID, this.pageSize);
      }

      // Use the API endpoint if there are no metafield reviews / turned off.
    } else {
      this.manager = new ReviewsIOStrategy(this.anyVariantSKU, this.pageSize);
    }
  }

  cacheSVGs() {
    this.shareSVG = `
      <svg width="15" height="13.125" viewBox="0 0 15 13.125">
        <path style="fill: #767676;" d="M14.757,6.874,9.6,2.421a.7.7,0,0,0-1.163.532V5.3C3.732,5.353,0,6.3,0,10.756a5.989,5.989,0,0,0,2.441,4.516.522.522,0,0,0,.823-.546c-1.328-4.248.63-5.376,5.174-5.442v2.576a.7.7,0,0,0,1.163.532l5.156-4.453A.7.7,0,0,0,14.757,6.874Z" transform="translate(0 -2.25)"/>
      </svg>
    `;
    this.starSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19.143" viewBox="0 0 20 19.143">
        <path data-name="Icon awesome-star" d="m10.368.665-2.441 4.95-5.462.8A1.2 1.2 0 0 0 1.8 8.452L5.755 12.3l-.935 5.439A1.2 1.2 0 0 0 6.555 19l4.886-2.568L16.327 19a1.2 1.2 0 0 0 1.735-1.26l-.935-5.44 3.951-3.85a1.2 1.2 0 0 0-.662-2.041l-5.462-.8-2.44-4.944a1.2 1.2 0 0 0-2.146 0z" transform="translate(-1.441 .001)" style="fill:#fbaf1f"/>
      </svg>
    `;
    this.emptyStarSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="21.028" height="20.162" viewBox="0 0 21.028 20.162">
        <path data-name="Icon awesome-star" d="m10.368.665-2.441 4.95-5.462.8A1.2 1.2 0 0 0 1.8 8.452L5.755 12.3l-.935 5.439A1.2 1.2 0 0 0 6.555 19l4.886-2.568L16.327 19a1.2 1.2 0 0 0 1.735-1.26l-.935-5.44 3.951-3.85a1.2 1.2 0 0 0-.662-2.041l-5.462-.8-2.44-4.944a1.2 1.2 0 0 0-2.146 0z" transform="translate(-.927 .501)" style="fill:none;stroke:#fbaf1f"/>
      </svg>
    `;
    this.greenCheckmarkSVG = `
      <svg data-name="Green checkmark with white bg" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
        <circle data-name="Ellipse 1" cx="7.5" cy="7.5" r="7.5" style="fill:#fff"/>
        <path data-name="Icon ionic-ios-checkmark-circle" d="M10.875 3.375a7.5 7.5 0 1 0 7.5 7.5 7.5 7.5 0 0 0-7.5-7.5zm3.84 5.425-4.821 4.844a.651.651 0 0 1-.418.2.631.631 0 0 1-.422-.206l-2.023-2.02a.144.144 0 0 1 0-.206l.642-.642a.139.139 0 0 1 .2 0l1.6 1.6 4.4-4.431a.142.142 0 0 1 .1-.043.131.131 0 0 1 .1.043l.631.653a.142.142 0 0 1 .011.208z" transform="translate(-3.375 -3.375)" style="fill:#30cc7f"/>
      </svg>
    `;
    this.thumbsUpSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
        <path data-name="Icon awesome-thumbs-up" d="M3.047 6.563H.7a.7.7 0 0 0-.7.7V14.3a.7.7 0 0 0 .7.7h2.347a.7.7 0 0 0 .7-.7V7.266a.7.7 0 0 0-.7-.703zm-1.172 7.265a.7.7 0 1 1 .7-.7.7.7 0 0 1-.7.7zM11.25 2.386c0 1.243-.761 1.94-.975 2.77h2.98A1.75 1.75 0 0 1 15 6.858a2.121 2.121 0 0 1-.57 1.442 2.447 2.447 0 0 1-.273 2.328 2.317 2.317 0 0 1-.48 2.19 1.555 1.555 0 0 1-.18 1.308c-.6.859-2.08.871-3.333.871h-.083a8.41 8.41 0 0 1-3.5-.929 4.611 4.611 0 0 0-1.548-.468.352.352 0 0 1-.345-.352V6.982a.351.351 0 0 1 .1-.25C5.952 5.586 6.451 4.372 7.4 3.419a3.548 3.548 0 0 0 .744-1.726c.13-.542.4-1.693.994-1.693.706 0 2.112.234 2.112 2.386z" style="fill:#767676"/>
      </svg>
    `;
    this.thumbsDownSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
        <path data-name="Icon awesome-thumbs-down" d="M0 1.641v7.031a.7.7 0 0 0 .7.7h2.347a.7.7 0 0 0 .7-.7V1.641a.7.7 0 0 0-.7-.7H.7a.7.7 0 0 0-.7.7zM1.172 7.5a.7.7 0 1 1 .7.7.7.7 0 0 1-.7-.7zM9.141 15c-.591 0-.864-1.151-.994-1.693a3.548 3.548 0 0 0-.747-1.726c-.951-.953-1.45-2.167-2.611-3.314a.351.351 0 0 1-.1-.25V1.755a.352.352 0 0 1 .344-.355A4.61 4.61 0 0 0 6.575.929 8.41 8.41 0 0 1 10.078 0h.083c1.253 0 2.735.012 3.333.871a1.555 1.555 0 0 1 .18 1.308 2.317 2.317 0 0 1 .48 2.19 2.447 2.447 0 0 1 .273 2.331A2.121 2.121 0 0 1 15 8.142a1.75 1.75 0 0 1-1.745 1.7h-2.98c.214.83.975 1.527.975 2.77C11.25 14.766 9.844 15 9.141 15z" style="fill:#767676"/>
      </svg>
    `;
    this.ellipseSVG = `
      <svg width="5" height="5" viewBox="0 0 5 5">
        <circle cx="2.5" cy="2.5" r="2.5" style="fill:#001233"/>
      </svg>
    `;
  }

  attachListeners() {
    this.onClickHandler = this.onClickHandler.bind(this);
    this.addEventListener('click', this.onClickHandler);
  }

  detachListeners() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClickHandler(e) {
    if (e.target.matches('[data-open-full-review-message]')) {
      const parentEl = e.target.closest('.StampedReviewItem__Contents');
      window.GlobalNPopup.loadWithContents(
        'stamped-full-message',
        parentEl.innerHTML
      );

      // Remove the new read more button
      const readMoreBtn = GlobalNPopup.popup.querySelector(
        '[data-open-full-review-message]'
      );
      readMoreBtn.remove();

      GlobalNPopup.open();

      // Pagination related listeners
    } else if (e.target.matches('.MSReviewsPagination__Bubble')) {
      this.changePage(parseInt(e.target.dataset.page));
    } else if (e.target.matches('.MSReviewsPagination__Prev')) {
      this.changePage(this.currentPage - 1);
    } else if (e.target.matches('.MSReviewsPagination__Next')) {
      this.changePage(this.currentPage + 1);
    }
  }

  async initReviews() {
    return this.renderSection();
  }

  async changePage(page) {
    this.currentPage = page;
    try {
      await this.renderSection();

      // Scroll to the top of the section (and a little before)
      const yOffset = -100;
      const y = this.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } catch (err) {
      console.warn('Review render error', err);
    }
  }

  async renderSection() {
    // Make sure the this.currentPage is greater than 0 and exists
    if (!this.currentPage || this.currentPage < 1) {
      this.currentPage = 0;
    }

    // Add loading css classes
    this.paginationArea.classList.add('MSReviewsPagination--loading');

    // Get the reviews response
    const reviewsResponse = await this.manager.getReviews(this.currentPage);
    if (reviewsResponse.results_count < 1) {
      this.paginationArea.classList.remove('MSReviewsPagination--loading');
      throw new Error('API did not return any results.');
    }

    // Make sure this currentPage is not greater than the number of pages
    const numPages = Math.ceil(reviewsResponse.results_count / this.pageSize);
    if (this.currentPage > numPages) {
      this.currentPage = numPages;
    }

    // Clear the placeholder stuff and render the reviews
    this.reviewsContainer.innerHTML = '';
    reviewsResponse.reviews.forEach(review => {
      const newReviewItem = this.renderReview(review);
      this.reviewsContainer.appendChild(newReviewItem);
    });

    // Render pagination buttons
    this.renderPagination(reviewsResponse);

    // Remove loading css classes
    this.paginationArea.classList.remove('MSReviewsPagination--loading');
  }

  /**
   * reviewsResponse is the response from this.getReviews (the public reviews
   * endpoint).
   */
  renderPagination(reviewsResponse) {
    const total = reviewsResponse.results_count;
    const numPages = Math.ceil(total / this.pageSize);
    let paginationHTML = '';

    // Start two pages behind the current page
    let pageIndex = this.currentPage - 2;
    while (pageIndex <= 0) {
      pageIndex += 1;
    }

    // Mobile only has 3 bubbles before the ellipsis.
    let numBubblesBeforeEllipsis = 5;
    if (window.innerWidth <= 768) {
      numBubblesBeforeEllipsis = 3;
    }

    for (let i = 0; i < numBubblesBeforeEllipsis; i++) {
      if (pageIndex >= numPages) {
        break;
      }

      const isActive = pageIndex === this.currentPage;
      paginationHTML += this.getPageBtnHTML(pageIndex, isActive);

      pageIndex += 1;
    }

    // Only show ellipsis if there is more than one page, and if there's
    // some distance before the last button and the last page's button.
    if (numPages > 1 && pageIndex !== numPages) {
      paginationHTML += this.getEllipsisHTML();
    }

    // Add the last page button
    const isLastPage = this.currentPage === numPages;
    paginationHTML += this.getPageBtnHTML(numPages, isLastPage);

    this.paginationLinks.innerHTML = paginationHTML;
  }

  getPageBtnHTML(page, active = false) {
    return `
      <button class="MSReviewsPagination__Bubble ${
        active ? 'MSReviewsPagination__Bubble--active' : ''
      }" data-page="${page}">
        ${page}
      </button>
    `;
  }

  getEllipsisHTML() {
    return `
      <span class="MSReviewsPagination__Ellipsis">
        ${this.ellipseSVG} ${this.ellipseSVG} ${this.ellipseSVG}
      </span>
    `;
  }

  renderReview(context) {
    const {
      order_id: orderID,
      rating,
      title,
      date_created: dateCreated,
      comments: reviewMessage,
      author,
      sku,
    } = context;
    const reviewDate = new Date(dateCreated).toLocaleDateString();
    const customerAttributes = author.attributes;
    const position = customerAttributes.find(option => {
      return option.label.includes('preferred sleep position?');
    });
    // Note that position is an array in a string that must be parsed
    let positionValue = null;
    if (position) {
      try {
        positionValue = JSON.parse(position.value);
        positionValue = positionValue.join(', ');
      } catch (err) {
        console.warn(`Invalid sleep position value for review of ${orderID}`);
        positionValue = null;
      }
    }
    const sleepStyle = customerAttributes.find(option => {
      return option.label.includes('sleep alone or with a partner?');
    });
    const age = customerAttributes.find(option => {
      return option.label.includes('What is your age?');
    });

    const starsHTML = this.getStarsHTML(rating);

    const newReviewItem = document.createElement('article');
    newReviewItem.classList.add('StampedReviewItem');
    newReviewItem.setAttribute('data-order-id', orderID);
    newReviewItem.setAttribute('data-sku', sku);

    // prettier-ignore
    newReviewItem.innerHTML = `
      <aside class="StampedPersonBlock">
        <header class="StampedPersonBlock__Header">
          <b>${author.name}</b>
        </header>
          <div class="StampedPersonBlock__CustomAnswers">
            <div class="StampedPersonBlock__QAInfoLine">
              <span class="StampedPersonBlock__QALabel">Position:</span>
              <span class="StampedPersonBlock__QAValue">
                ${positionValue ? this.truncateByWords(positionValue, 15) : '--'}
              </span>
            </div>
            <div class="StampedPersonBlock__QAInfoLine">
              <span class="StampedPersonBlock__QALabel">Age:</span>
              <span class="StampedPersonBlock__QAValue">
                ${age ? this.truncateByWords(age.value, 10) : '--'}
              </span>
            </div>
            <div class="StampedPersonBlock__QAInfoLine">
              <span class="StampedPersonBlock__QALabel">Sleep:</span>
              <span class="StampedPersonBlock__QAValue">
                ${
                  sleepStyle ? this.truncateByWords(sleepStyle.value, 15) : '--'
                }
              </span>
            </div>
          </div>
        <footer class="StampedPersonBlock__Footer">
          <span>
            ${this.greenCheckmarkSVG}
            Verified Buyer
          </span>
          <b class="Color--orange">&ensp;|&ensp;</b>
          <span class="StampedPersonBlock__Date">${reviewDate}</span>
        </footer>
      </aside>
      <div class="StampedReviewItem__Contents">
        <div class="StampedReviewItem__Stars">
          ${starsHTML}
        </div>
          <h3 class="StampedReviewItem__Title">
            ${title
              ? title
              : this.truncateByWords(reviewMessage, 30)
            }
          </h3>
        <div class="StampedReviewItem__Message">
          ${reviewMessage ? reviewMessage : "<i>Author didn't leave any comments.</i>"}
        </div>
        ${
          reviewMessage.length >= 105
          ? (`<button data-open-full-review-message class="StampedReviewItem__ReadMoreBtn">
              Read More
            </button>`)
          : ''
        }
        <footer class="StampedReviewItem__Footer">
        ${
          '' /*
          <div class="StampedReviewItem__ShareLabel">
            <a href="https://www.facebook.com/dialog/share?app_id=222664514734026&display=popup&href=https%3a%2f%2fwww.nolahmattress.com%2fa%2freview%2ffacebookpost%3freviewId%3d${id}&redirect_uri=https://stamped.io/shares?review_id=${id}" class="StampedReviewItem__ShareBtn" target="_blank" rel="nofollow noopener" aria-label="Share review on Facebook">
              Share ${this.shareSVG}
            </a>
          </div>
        */
        }
        </footer>
      </div>
    `;

    return newReviewItem;
  }

  /**
   * Out of 5.
   */
  getStarsHTML(rating) {
    let starsHTML = '';
    const numEmptyStars = 5 - rating;
    for (let i = 0; i < rating; i++) {
      starsHTML = starsHTML + this.starSVG;
    }
    for (let i = 0; i < numEmptyStars; i++) {
      starsHTML = starsHTML + this.emptyStarSVG;
    }

    return starsHTML;
  }

  truncateByWords(str, maxLength) {
    if (str.length <= maxLength) {
      return str;
    }

    const strWithSpaceAtEnd = str + ' ';
    let truncatedStr = strWithSpaceAtEnd.substr(0, maxLength);
    truncatedStr = truncatedStr.substr(
      0,
      Math.min(truncatedStr.length, truncatedStr.lastIndexOf(' '))
    );

    return truncatedStr + '...';
  }
}
customElements.define('mattress-stamped-reviews', MatStampedReviews);
